import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import { MessageSquareText, Trash2, X } from "lucide-react";
import { ImperialFieldResultDialog, MetricFieldResultDialog } from "./FieldResultDialogs";
function heightSeed(height, metric) {
    if (metric)
        return Number(height);
    const [feet = "0", inches = "0"] = height.split("-");
    return Math.round((Number(feet) + Number(inches) / 12) * 3048) / 10000;
}
function heightKey(seed) {
    return seed.toFixed(6);
}
function storedHeight(height, metric) {
    if (metric) {
        const value = Number(height);
        return Number.isFinite(value) ? value.toFixed(2) : height;
    }
    const match = height.match(/^(\d+)-(\d{1,2})(?:\.(\d{1,2}))?$/);
    if (!match)
        return height;
    return `${Number(match[1])}-${match[2].padStart(2, "0")}.${(match[3] ?? "00").padEnd(2, "0")}`;
}
function parseHeightAttempt(value, metric) {
    const [height, ...rawMarks] = String(value).split("%");
    const seed = heightSeed(height, metric);
    if (!height || !Number.isFinite(seed))
        return null;
    const marks = rawMarks.filter((mark) => mark === "O" || mark === "X" || mark === "P").slice(0, 3);
    return { height, seed, marks };
}
function parseEventHeights(value, metric) {
    if (value == null)
        return [];
    const values = Array.isArray(value)
        ? value.map((entry, index) => [String(index), entry])
        : typeof value === "object"
            ? Object.entries(value)
            : [["0", value]];
    return values.map(([key, storedValue]) => {
        const stored = storedValue && typeof storedValue === "object" ? storedValue : null;
        const height = stored?.height ?? stored?.result ?? stored?.value ?? (typeof storedValue === "boolean" ? key : storedValue);
        if (typeof height !== "string" && typeof height !== "number")
            return null;
        const parsed = parseHeightAttempt(height, metric);
        if (!parsed)
            return null;
        return stored?.seed != null && Number.isFinite(Number(stored.seed)) ? { ...parsed, seed: Number(stored.seed) } : parsed;
    }).filter((entry) => entry != null);
}
function displayHeight(height, metric) {
    if (height == null)
        return "-";
    const value = String(height);
    if (metric)
        return `${value}m`;
    const match = value.match(/^(\d+)-(\d{1,2}(?:\.\d+)?)$/);
    return match ? `${Number(match[1])}'-${Number(match[2])}\"` : value;
}
function missesBeforeHeight(entries, targetSeed) {
    let consecutiveMisses = 0;
    const priorEntries = [...entries].filter((entry) => entry.seed < targetSeed).sort((left, right) => left.seed - right.seed);
    for (const entry of priorEntries) {
        for (const mark of entry.marks) {
            if (mark === "O")
                consecutiveMisses = 0;
            else if (mark === "X")
                consecutiveMisses += 1;
            if (consecutiveMisses >= 3)
                return 3;
        }
    }
    return consecutiveMisses;
}
function HeightMarksDialog({ athleteName, height, initialMarks, carriedMisses, onClose, onSave }) {
    const [marks, setMarks] = useState(initialMarks);
    const availableAttempts = Math.max(0, 3 - carriedMisses);
    function setMark(index, mark) {
        if (index >= availableAttempts)
            return;
        setMarks((current) => {
            if (index > 0 && !current[index - 1])
                return current;
            const next = current.slice(0, index);
            next[index] = mark;
            return mark === "O" || mark === "P" ? next : next.concat(current.slice(index + 1, 3));
        });
    }
    return _jsx("div", { className: "height-marks-backdrop", role: "presentation", onMouseDown: onClose, children: _jsxs("section", { className: "height-marks-dialog", role: "dialog", "aria-modal": "true", "aria-labelledby": "height-marks-title", onMouseDown: (event) => event.stopPropagation(), children: [_jsx("button", { className: "height-marks-close", type: "button", onClick: onClose, "aria-label": "Close", children: _jsx(X, { size: 20 }) }), _jsxs("h2", { id: "height-marks-title", children: [height, " Attempts"] }), _jsxs("p", { children: [athleteName, carriedMisses > 0 ? ` - ${carriedMisses} ${carriedMisses === 1 ? "miss" : "misses"} carried from the previous height` : ""] }), _jsx("div", { className: "height-marks-editor", children: [0, 1, 2].map((index) => {
                        const disabled = index >= availableAttempts || (index > 0 && (!marks[index - 1] || marks.slice(0, index).some((mark) => mark === "O" || mark === "P")));
                        return _jsxs("div", { children: [_jsxs("strong", { children: ["Attempt ", index + 1] }), _jsx("span", { children: ["O", "X", "P"].map((mark) => _jsx("button", { className: marks[index] === mark ? `height-choice height-choice--${mark.toLowerCase()}` : "height-choice", type: "button", disabled: disabled, onClick: () => setMark(index, mark), children: mark }, mark)) })] }, index);
                    }) }), _jsxs("footer", { children: [_jsx("button", { className: "height-marks-clear", type: "button", onClick: () => setMarks([]), children: "Clear" }), _jsx("span", {}), _jsx("button", { type: "button", onClick: onClose, children: "Cancel" }), _jsx("button", { type: "button", onClick: () => { onSave(marks); onClose(); }, children: "Save" })] })] }) });
}
export function VerticalFieldResultsGrid({ eventName, metric, eventHeights, addHeightRequest = 0, rows, readOnly = false, showActions = true, canDelete = !readOnly, disabledTitle, onNotes, onDelete, onHeightsUpdate, onUpdate }) {
    const rowsKey = JSON.stringify(rows);
    const entriesFromRows = () => Object.fromEntries(rows.map((row) => [row.id, row.attempts.map((attempt) => parseHeightAttempt(attempt, metric)).filter((attempt) => attempt != null)]));
    const [entries, setEntries] = useState(entriesFromRows);
    const [addedHeights, setAddedHeights] = useState([]);
    const [editing, setEditing] = useState(null);
    const [addingHeight, setAddingHeight] = useState(false);
    const [error, setError] = useState("");
    const configuredHeights = useMemo(() => parseEventHeights(eventHeights, metric), [eventHeights, metric]);
    useEffect(() => setEntries(entriesFromRows()), [metric, rowsKey]);
    useEffect(() => {
        if (addHeightRequest > 0 && !readOnly) {
            setError("");
            setAddingHeight(true);
        }
    }, [addHeightRequest, readOnly]);
    const heights = useMemo(() => {
        const unique = new Map();
        configuredHeights.forEach((entry) => unique.set(heightKey(entry.seed), entry));
        addedHeights.forEach((entry) => unique.set(heightKey(entry.seed), entry));
        return [...unique.values()].sort((left, right) => left.seed - right.seed);
    }, [addedHeights, configuredHeights]);
    const sortedRows = useMemo(() => [...rows].sort((left, right) => Number(right.seed ?? Number.NEGATIVE_INFINITY) - Number(left.seed ?? Number.NEGATIVE_INFINITY) || left.displayName.localeCompare(right.displayName)), [rows]);
    const hasHeightProgression = heights.length > 0;
    const gridColumns = hasHeightProgression
        ? `minmax(var(--vertical-athlete-column,175px),1.5fr) minmax(var(--vertical-result-column,92px),1fr) repeat(${heights.length},minmax(var(--vertical-attempt-column,78px),1fr))`
        : "minmax(0,1fr) minmax(72px,92px)";
    const gridStyle = {
        "--vertical-grid-min-width": hasHeightProgression ? `${Math.max(500, 267 + heights.length * 78)}px` : "0px",
        "--vertical-mobile-grid-min-width": `${Math.max(350, 190 + heights.length * 52)}px`,
    };
    function saveMarks(rowId, entry, marks) {
        const athleteEntries = entries[rowId] ?? [];
        const exists = athleteEntries.some((value) => heightKey(value.seed) === heightKey(entry.seed));
        const nextEntries = exists ? athleteEntries.map((value) => heightKey(value.seed) === heightKey(entry.seed) ? { ...value, marks } : value) : [...athleteEntries, { ...entry, marks }];
        setEntries((current) => ({ ...current, [rowId]: nextEntries }));
        const cleared = nextEntries.filter((value) => value.marks.includes("O")).sort((left, right) => right.seed - left.seed)[0];
        const hasMiss = nextEntries.some((value) => value.marks.includes("X"));
        const result = cleared?.height ?? (hasMiss ? "NH" : null);
        const attempts = nextEntries
            .filter((value) => value.marks.length > 0)
            .sort((left, right) => left.seed - right.seed)
            .map((value) => `${value.height}${value.marks.map((mark) => `%${mark}`).join("")}`);
        setError("");
        void onUpdate(rowId, { result, seed: cleared?.seed ?? (hasMiss ? -1 : null), attempts }).catch((caughtError) => {
            setEntries((current) => ({ ...current, [rowId]: athleteEntries }));
            setError(caughtError instanceof Error ? caughtError.message : "The attempts could not be saved.");
        });
    }
    function addHeight(value) {
        if (!value.result || value.seed == null)
            return;
        const previousMaximum = Math.max(...heights.map((entry) => entry.seed), Number.NEGATIVE_INFINITY);
        if (value.seed <= previousMaximum) {
            setError("The new height must be greater than the current highest height.");
            return;
        }
        const entry = { height: storedHeight(value.result, metric), seed: value.seed, marks: [] };
        const nextHeights = [...heights, entry]
            .sort((left, right) => left.seed - right.seed)
            .map((height) => height.height);
        setAddedHeights((current) => [...current, entry]);
        setError("");
        void onHeightsUpdate(nextHeights).catch((caughtError) => {
            setAddedHeights((current) => current.filter((height) => heightKey(height.seed) !== heightKey(entry.seed)));
            setError(caughtError instanceof Error ? caughtError.message : "The height could not be saved.");
        });
    }
    return _jsxs("section", { className: "vertical-field-grid-wrap", children: [_jsxs("div", { className: `vertical-field-grid ${hasHeightProgression ? "" : "vertical-field-grid--summary"}`, style: gridStyle, children: [_jsxs("div", { className: "vertical-field-grid__header", style: { gridTemplateColumns: gridColumns }, children: [_jsx("strong", { children: "Athlete" }), _jsx("strong", { children: "Height" }), heights.map((height) => _jsx("strong", { children: displayHeight(height.height, metric) }, heightKey(height.seed)))] }), sortedRows.map((row) => {
                        const athleteEntries = entries[row.id] ?? [];
                        return _jsxs("div", { className: "vertical-field-grid__row", style: { gridTemplateColumns: gridColumns }, "data-value": row.id, children: [_jsxs("div", { className: "horizontal-field-grid__athlete", children: [_jsx("strong", { title: row.displayName, children: row.displayName }), showActions && _jsxs("span", { children: [_jsx("button", { type: "button", disabled: readOnly, title: readOnly ? disabledTitle : "Add or edit note", "aria-label": `Notes for ${row.dialogName}`, onClick: () => onNotes(row.id), children: _jsx(MessageSquareText, { size: 13 }) }), _jsx("button", { type: "button", disabled: !canDelete, title: canDelete ? "Remove athlete from event" : disabledTitle, "aria-label": `Remove ${row.dialogName}`, onClick: () => onDelete(row.id), children: _jsx(Trash2, { size: 13 }) })] }), row.comment && _jsx("small", { title: row.comment, children: row.comment })] }), _jsx("strong", { className: "vertical-field-grid__best", children: displayHeight(row.result, metric) }), heights.map((height) => {
                                    const existing = athleteEntries.find((entry) => heightKey(entry.seed) === heightKey(height.seed));
                                    const entry = existing ?? { ...height, marks: [] };
                                    const eliminated = !existing && missesBeforeHeight(athleteEntries, height.seed) >= 3;
                                    return _jsx("button", { className: "vertical-height-cell", type: "button", disabled: readOnly || eliminated, title: readOnly ? disabledTitle : eliminated ? "The athlete is out after three consecutive misses." : undefined, onClick: () => setEditing({ row, entry }), children: existing && existing.marks.length > 0 ? existing.marks.join("") : "-" }, heightKey(height.seed));
                                })] }, row.id);
                    })] }), error && _jsx("div", { className: "meet-form-error vertical-field-grid__error", children: error }), editing && _jsx(HeightMarksDialog, { athleteName: editing.row.dialogName, height: displayHeight(editing.entry.height, metric), initialMarks: editing.entry.marks, carriedMisses: missesBeforeHeight(entries[editing.row.id] ?? [], editing.entry.seed), onClose: () => setEditing(null), onSave: (marks) => saveMarks(editing.row.id, editing.entry, marks) }), addingHeight && (metric ? _jsx(MetricFieldResultDialog, { athleteName: "Event height", eventName: eventName, resultType: "Height", allowSpecialResults: false, onSubmit: addHeight, onClose: () => setAddingHeight(false) }) : _jsx(ImperialFieldResultDialog, { athleteName: "Event height", eventName: eventName, resultType: "Height", quarterInches: true, allowSpecialResults: false, onSubmit: addHeight, onClose: () => setAddingHeight(false) }))] });
}
